import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../constants/paths.ts';
import { useMarkNotificationsAsRegisteredMutation } from '../graphql/generated/operations/messages.ts';

export interface MobileContextProps {
  sendMessage: (topic: string, message: string, data?: any) => void;
  notificationPermission: 'unknown' | 'denied' | 'granted';
}

export const MobileContext = createContext<MobileContextProps>({} as MobileContextProps);

export interface MobileProviderProps {
  children: ReactNode;
}

export const MobileProvider = ({ children }: MobileProviderProps) => {
  const navigate = useNavigate();
  const [registered] = useMarkNotificationsAsRegisteredMutation();
  const [notificationPermission, setNotificationPermission] = useState<'unknown' | 'denied' | 'granted'>('unknown');

  useEffect(() => {
    const handleEvent = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data); // Parse incoming JSON string
        if (message.topic === 'NotificationPermissionsDenied') {
          setNotificationPermission('denied');
          const dismissed = window.sessionStorage.getItem('notifications_prompt_dismissed') === '1';
          if (!dismissed) {
            navigate(Paths.ENABLE_NOTIFICATIONS);
          }
        } else if (message.topic === 'NotificationGranted') {
          setNotificationPermission('granted');
          window.sessionStorage.removeItem('notifications_prompt_dismissed');
          const postRoute = window.sessionStorage.getItem('post_notification_route');
          if (postRoute) {
            window.sessionStorage.removeItem('post_notification_route');
            navigate(postRoute);
          } else {
            navigate(Paths.ENABLE_TRACKING);
          }
        } else if (message.topic === 'TrackingNext') {
          navigate(Paths.HOME);
        } else if (message.topic === 'NotificationsScheduled') {
          registered({
            variables: {
              notificationIds: message.data
            }
          });
        } else if (message.topic === 'ActivityNotification') {
        } else if (message.topic === 'WeeklyLogNotification') {
          navigate(Paths.WEEKLY_LOG);
        } else if (message.topic === 'DailyLogNotification') {
          navigate(Paths.DAILY_LOG);
        } else if (message.topic === 'MessageNotification') {
          navigate(Paths.MESSAGES);
        }
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    window.addEventListener('message', handleEvent); // Use window instead of document

    return () => window.removeEventListener('message', handleEvent);
  }, []);

  const sendMessage = (topic: string, message: string, data?: any) => {
    if (!!(window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({ topic, message, data }));
    }
  };

  return <MobileContext.Provider value={{ sendMessage, notificationPermission }}>{children}</MobileContext.Provider>;
};

export const useMobile = () => useContext(MobileContext);
